import Vue from 'vue'

import * as types from '../mutation-types'
import { fetchStatuses } from '@/mixins/fetch-status'

import polls from '@/api/polls'

const state = {
  polls: {},
  pollUserOptionSummaries: {},
  pollUserOptions: [],
  pollUserOptionsStatus: fetchStatuses.FetchStatusIdle,
  pollUserOptionsStatusError: null,
  pollQuestions: [],
  pollQuestionsStatus: fetchStatuses.FetchStatusIdle,
  pollQuestionsStatusError: null,
  pollQuestionsFilter: [],
  pollQuestionsFilterStatus: fetchStatuses.FetchStatusIdle,
  pollQuestionsFilterStatusError: null
}

const getters = {
  communityPolls: (state) => {
    return state.polls
  },
  getPoll: (state) => (id) => {
    const key = pollKey(id)
    return state.polls[key]
  },
  getPollUserOptionSummary: (state) => (postId) => {
    return state.pollUserOptionSummaries[postId]
  },
  isPollCompleted: (state) => {
    const options = Object.keys(state.pollUserOptionSummaries).map((key) => state.pollUserOptionSummaries[key])
    return !options.some((item) => item.total !== item.completed)
  },
  hasQuestions: (state) => {
    return state.pollQuestionsStatus === fetchStatuses.FetchStatusLoaded && state.pollQuestions.length > 0
  },
  getPollUserOptions: (state, getters) => (pollID) => {
    if (!getters.hasQuestions || state.pollUserOptionsStatus !== fetchStatuses.FetchStatusLoaded || state.pollUserOptions.length === 0) {
      return []
    }

    return state.pollUserOptions.filter(m => m.pollID === pollID)
  },
  getQuestionsWithAnswers: (state, getters) => {
    return state.pollQuestions.map(m => {
      return {
        ...m,
        answers: getters.getPollUserOptions(m.id)
      }
    })
  },
  getQuestionsFilter: (state) => {
    if (state.pollQuestionsFilterStatus === fetchStatuses.FetchStatusLoaded) {
      return state.pollQuestionsFilter
    }
    return []
  }
}

// actions
const actions = {
  fetchPolls ({ commit, state }, { limit, offset }) {
    return new Promise((resolve, reject) => {
      polls.getCommunityPolls(limit, offset).then((response) => {
        const polls = response.data.result.polls
        polls.forEach((poll) => {
          commit(types.ADD_COMMUNITY_POLL, poll)
        })
        resolve()
      }, (error) => {
        reject(error)
      })
    })
  },
  getUserAnswer ({ commit }, { pollID }) {
    return new Promise((resolve, reject) => {
      polls.getUserResponse(pollID).then((response) => {
        resolve(response)
      }, (error) => {
        reject(error)
      })
    })
  },
  getUserAnswers ({ commit }, { pollID }) {
    return new Promise((resolve, reject) => {
      polls.getAllUserResponses(pollID).then((response) => {
        resolve(response)
      }, (error) => {
        reject(error)
      })
    })
  },
  getOtherUserAnswers ({ commit }, { pollID, userID, projectID }) {
    return new Promise((resolve, reject) => {
      polls.getOtherUsersResponses(pollID, userID, projectID).then((response) => {
        resolve(response)
      }, (error) => {
        reject(error)
      })
    })
  },
  saveUserOption ({ commit }, data) {
    return new Promise((resolve, reject) => {
      polls.saveUserOption(data).then((response) => {
        resolve(response)
      }, (error) => {
        reject(error)
      })
    })
  },
  saveOtherUserOption ({ commit }, data) {
    return new Promise((resolve, reject) => {
      polls.saveOtherUserOption(data.userID, data).then((response) => {
        resolve(response)
      }, (error) => {
        reject(error)
      })
    })
  },
  getPollUserOptionSummaries ({ commit }, { projectId }) {
    polls.getPollUserOptionSummaries(projectId).then(response => {
      commit(types.INIT_POLL_USRE_OPTION_SUMMARIES, response.data.result.summaries)
    })
  },
  fetchPollUserOptions ({ commit }, { projectId }) {
    commit(types.POLL_USER_OPTIONS_LOADING)
    polls.getPollUserOptions(projectId).then(response => {
      commit(types.POLL_USER_OPTIONS_LOADED, response.data.result.pollUserOptions)
    }, error => {
      commit(types.POLL_USER_OPTIONS_ERROR, error)
    })
  },
  fetchPollQuestions ({ commit }, { projectId }) {
    commit(types.POLL_QUESTIONS_LOADING)
    polls.getPollQuestions(projectId).then(response => {
      commit(types.POLL_QUESTIONS_LOADED, response.data.result.polls)
    }, error => {
      commit(types.POLL_QUESTIONS_ERROR, error)
    })
  },
  fetchPollQuestionsFilter ({ commit }, { projectIDs }) {
    commit(types.POLL_QUESTIONS_FILTER_LOADING)
    polls.getPollQuestionsFilter(projectIDs).then(response => {
      commit(types.POLL_QUESTIONS_FILTER_LOADED, response.data.result.filter)
    }, error => {
      commit(types.POLL_QUESTIONS_FILTER_ERROR, error)
    })
  }
}

const mutations = {
  [types.ADD_COMMUNITY_POLL] (state, poll) {
    const key = pollKey(poll.id)
    if (!state.polls[key]) {
      poll.options = poll.options || []
      Vue.set(state.polls, key, poll)
    }
  },
  [types.INIT_POLL_USRE_OPTION_SUMMARIES] (state, summaries) {
    state.pollUserOptionSummaries = summaries.reduce((a, x) => ({ ...a, [x.postID]: x }), {})
  },
  [types.POLL_QUESTIONS_IDLE] (state) {
    state.pollQuestionsStatus = fetchStatuses.FetchStatusIdle
  },
  [types.POLL_QUESTIONS_LOADING] (state) {
    state.pollQuestionsStatus = fetchStatuses.FetchStatusLoading
  },
  [types.POLL_QUESTIONS_LOADED] (state, polls) {
    state.pollQuestionsStatus = fetchStatuses.FetchStatusLoaded
    state.pollQuestions = polls
  },
  [types.POLL_QUESTIONS_ERROR] (state, msg) {
    state.pollQuestionsStatus = fetchStatuses.FetchStatusError
    state.pollQuestionsStatusError = msg
  },
  [types.POLL_QUESTIONS_FILTER_IDLE] (state) {
    state.pollQuestionsFilterStatus = fetchStatuses.FetchStatusIdle
  },
  [types.POLL_QUESTIONS_FILTER_LOADING] (state) {
    state.pollQuestionsFilterStatus = fetchStatuses.FetchStatusLoading
  },
  [types.POLL_QUESTIONS_FILTER_LOADED] (state, filter) {
    state.pollQuestionsFilterStatus = fetchStatuses.FetchStatusLoaded
    state.pollQuestionsFilter = filter
  },
  [types.POLL_QUESTIONS_FILTER_ERROR] (state, msg) {
    state.pollQuestionsFilterStatus = fetchStatuses.FetchStatusError
    state.pollQuestionsFilterStatusError = msg
  },
  [types.POLL_USER_OPTIONS_IDLE] (state) {
    state.pollUserOptionsStatus = fetchStatuses.FetchStatusIdle
  },
  [types.POLL_USER_OPTIONS_LOADING] (state) {
    state.pollUserOptionsStatus = fetchStatuses.FetchStatusLoading
  },
  [types.POLL_USER_OPTIONS_LOADED] (state, options) {
    state.pollUserOptionsStatus = fetchStatuses.FetchStatusLoaded
    state.pollUserOptions = options
  },
  [types.POLL_USER_OPTIONS_ERROR] (state, msg) {
    state.pollUserOptionsStatus = fetchStatuses.FetchStatusError
    state.pollUserOptionsStatusError = msg
  }
}

const pollKey = function (id) {
  return `c-${id}`
}

export default {
  state,
  getters,
  actions,
  mutations
}
